import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {useWindowResize} from '@ozgene/gp5.hooks.use-window-resize';
import {
  IntegratedSorting,
  SortingState,
  SelectionState,
  FilteringState,
  IntegratedFiltering,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  VirtualTable,
  TableHeaderRow,
  TableSelection,
  TableFilterRow,
} from '@devexpress/dx-react-grid-material-ui';
import {ColumnDefinedFilterCell} from 'util/components/ColumnDefinedFilterCell';
import {CustomRowComponent} from 'util/components/CustomRowComponent';
import {CustomNoDataCell} from 'util/components/CustomNoDataCell';
import {ColumnDefinedCell} from 'util/components/ColumnDefinedCell';

const importedProcedureTableColumns = [
  {
    name: 'name',
    title: 'Name',
    getCellValue: (row) => row.cnsProcedure?.name,
  },
  {
    name: 'milestone',
    title: 'Milestone',
    getCellValue: (row) => row.milestone.name,
  },
];

function ImportedProcedureList(props) {
  const {
    procedures,
    onSelectionChange,
    procedureFilterText,
    milestoneFilterText,
    onChangeProcedureFilterText,
    onChangeMilestoneFilterText,
    selectedId,
  } = props;
  const [filters, setFilters] = useState([
    procedureFilterText.length > 0 && {
      columnName: 'name',
      operation: 'contains',
      value: procedureFilterText,
    },
    milestoneFilterText.length > 0 && {
      columnName: 'milestone',
      operation: 'contains',
      value: milestoneFilterText,
    },
  ]);
  const {height} = useWindowResize();
  const handleFiltersChange = useCallback(
    (newFilters) => {
      const procedureFilter = newFilters.find(({columnName}) => columnName === 'name');
      const milestoneFilter = newFilters.find(({columnName}) => columnName === 'milestone');
      const procedureFilterValue = procedureFilter?.value ?? '';
      const milestoneFilterValue = milestoneFilter?.value ?? '';

      if (procedureFilterValue !== procedureFilterText) {
        onChangeProcedureFilterText(procedureFilterValue);
      }

      if (milestoneFilterValue !== milestoneFilterText) {
        onChangeMilestoneFilterText(milestoneFilterValue);
      }

      setFilters(newFilters);
    },
    [
      milestoneFilterText,
      onChangeMilestoneFilterText,
      onChangeProcedureFilterText,
      procedureFilterText,
    ],
  );

  return (
    <Grid rows={procedures} columns={importedProcedureTableColumns} getRowId={({id}) => id}>
      <FilteringState filters={filters} onFiltersChange={handleFiltersChange} />
      <SortingState defaultSorting={[{columnName: 'name', direction: 'asc'}]} />
      <SelectionState selection={[selectedId]} onSelectionChange={onSelectionChange} />
      <IntegratedFiltering />
      <IntegratedSorting />
      <VirtualTable
        height={height - 140}
        cellComponent={ColumnDefinedCell}
        noDataCellComponent={CustomNoDataCell}
      />
      <TableHeaderRow showSortingControls />
      <TableSelection
        selectByRowClick
        highlightRow
        showSelectionColumn={false}
        rowComponent={CustomRowComponent}
      />
      <TableFilterRow showFilterSelector cellComponent={ColumnDefinedFilterCell} />
    </Grid>
  );
}

ImportedProcedureList.propTypes = {
  procedures: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  procedureFilterText: PropTypes.string.isRequired,
  milestoneFilterText: PropTypes.string.isRequired,
  onChangeProcedureFilterText: PropTypes.func.isRequired,
  onChangeMilestoneFilterText: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
};

export default ImportedProcedureList;
